import { useEffect, useRef, useState } from 'react';
import { usePlugins } from '@morph-mapper/plugins';
import { t } from 'i18next';
import { useStore } from '@/store';
import { useNotify, useReevaluate } from '@/hooks';
import {
  defaultSelection,
  filledSelection,
  listEvaluableFields,
  type EvaluableField,
} from '../../../../utils/reevaluation';
import {
  setAllPluginsEnabled,
  togglePluginEnabled,
  withPluginSetting,
  type PluginConfigItem,
} from '../../../../utils/plugin-config';

export const useReevaluateModal = (opened: boolean, onClose: () => void) => {
  const { currentConfig, hydrate } = usePlugins();
  const { reevaluate } = useReevaluate();
  const { success, error } = useNotify();
  const [getEntries, hasValue] = useStore(({ entries: e }) => [
    e.getEntries,
    e.hasValue,
  ]);
  const staleFields = useStore(({ ai }) => ai.staleFields);

  // All evaluable fields; the list in the dialog leaves out the iterators,
  // which the "re-detect iterators" switch covers.
  const [allFields, setAllFields] = useState<EvaluableField[]>([]);
  const fields = allFields.filter((f) => !f.iterator);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [redetectIterators, setRedetectIterators] = useState(false);
  const [plugins, setPlugins] = useState<PluginConfigItem[]>([]);
  const initialPlugins = useRef('');

  // Start from the current template and plugin setup every time it opens.
  useEffect(() => {
    if (!opened) return;
    const list = listEvaluableFields(getEntries(), hasValue, staleFields);
    setAllFields(list);
    setSelected(defaultSelection(list));
    setRedetectIterators(false);

    const config = currentConfig().plugins;
    setPlugins(config);
    initialPlugins.current = JSON.stringify(config);
  }, [opened]);

  const toggleField = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const selectEmpty = () => setSelected(defaultSelection(fields));
  const selectFilled = () => setSelected(filledSelection(fields));
  const selectAll = () => setSelected(new Set(fields.map((f) => f.id)));
  const selectNone = () => setSelected(new Set());

  const allSelected = fields.length > 0 && selected.size === fields.length;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => (allSelected ? selectNone() : selectAll());

  const togglePlugin = (key: string) =>
    setPlugins((prev) => togglePluginEnabled(prev, key));
  const setAllPlugins = (enabled: boolean) =>
    setPlugins((prev) => setAllPluginsEnabled(prev, enabled));
  const setPluginSetting = (
    pluginKey: string,
    settingKey: string,
    value: unknown,
  ) =>
    setPlugins((prev) => withPluginSetting(prev, pluginKey, settingKey, value));

  const canRun = selected.size > 0 || redetectIterators;

  const run = async () => {
    // Keep tree order, so iterators are evaluated before the fields below.
    const fieldIds = allFields
      .filter((f) => selected.has(f.id) || (redetectIterators && f.iterator))
      .map((f) => f.id);
    // hydrate() resets the plugins, so only do it when something changed.
    if (JSON.stringify(plugins) !== initialPlugins.current) hydrate(plugins);
    onClose();

    try {
      await reevaluate(fieldIds);
      success(t('reevaluate.done'));
    } catch (e) {
      error(
        t('reevaluate.failed', {
          message: e instanceof Error ? e.message : String(e),
        }),
      );
    }
  };

  return {
    fields,
    selected,
    toggleField,
    selectEmpty,
    selectFilled,
    allSelected,
    someSelected,
    toggleAll,
    redetectIterators,
    setRedetectIterators,
    selectAll,
    selectNone,
    plugins,
    togglePlugin,
    setAllPlugins,
    setPluginSetting,
    canRun,
    run,
  };
};
